// Shared formatting helpers for UGC Explorer.

// TikTok's signed profile URLs are now served as HEIC (TOP_INFLUENCER_FILTERS.md
// §7) - no browser other than Safari decodes HEIC in an <img>, so left alone
// these just render as a broken image. `.heic`/`.heif` can sit right before
// the querystring of a signed URL (`....heic?x-tos-...`), so this checks the
// path rather than the URL's tail.
const HEIC_EXTENSION = /\.heic(?:[?#]|$)|\.heif(?:[?#]|$)/i
export const isHeicUrl = (url) => {
  if (!url) return false
  return HEIC_EXTENSION.test(url)
}

// Instagram's CDN hotlink-protects its thumbnail/profile images - it 403s a
// browser <img> request outright, even with referrerPolicy="no-referrer"
// (confirmed against a live build: every reel thumbnail failed identically,
// with the browser falling back to rendering the image's `alt` text over the
// card). images.weserv.nl (wsrv.nl) is a well-known, no-signup mirror/resize
// proxy - it fetches the image server-side and re-serves it from its own
// origin, so the request that actually reaches Instagram's CDN never comes
// from this site's browser, and the hotlink check never gets a chance to
// reject it. The original URL is passed through `encodeURIComponent` because
// Instagram's CDN URLs carry their own signed `&`/`=` query params, which
// would otherwise be parsed as wsrv's own params and break the signature.
//
// The same proxy also happens to fix the HEIC case above: wsrv.nl decodes
// HEIC server-side (libvips/libheif) and re-encodes whatever it fetches, so
// `&output=jpg` is added for every proxied image - a no-op transcode for the
// already-JPEG Instagram case, and the only way a HEIC source ever reaches
// the browser as something it can actually display.
export const proxiedImageUrl = (url) => {
  if (!url) return null
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&output=jpg`
}

// Instagram's own CDN (`*.cdninstagram.com` / `*.fbcdn.net`) hotlink-protects
// images, and TikTok's signed URLs now serve HEIC - both need the wsrv.nl
// proxy above. This app's own S3-hosted mirrors (`thumb_url`/
// `profile_thumb_url` - see normalize.js) load directly and don't need the
// proxy at all, so callers check this per-URL instead of proxying
// everything Instagram/TikTok-related on the assumption it's still a raw,
// unmirrored platform link.
export const needsImageProxy = (url) => {
  if (!url) return false
  return /cdninstagram\.com|fbcdn\.net/i.test(url) || isHeicUrl(url)
}

// YouTube-style compact view counts - 1,950,384 -> "1.9M", 762,344 -> "762.3K".
// Truncated (not rounded) to one decimal, same as YouTube's own display, so
// `Intl.NumberFormat`'s "compact" notation isn't used here - it rounds
// (1,950,384 -> "2M"), which reads as a different number than the real count.
// `metrics.views` is the only populated metric (likes/comments are always null).
export const formatViews = (views) => {
  if (views === null || views === undefined) return "—"
  const num = Number(views)
  if (Number.isNaN(num)) return "—"
  if (num < 1000) return String(num)

  const units = [
    { value: 1e9, suffix: "B" },
    { value: 1e6, suffix: "M" },
    { value: 1e3, suffix: "K" },
  ]

  const unit = units.find(u => num >= u.value)
  const truncated = Math.floor((num / unit.value) * 10) / 10
  const formatted = Number.isInteger(truncated) ? String(truncated) : truncated.toFixed(1)
  return `${formatted}${unit.suffix}`
}

// `last_completed_at` / `next_refresh_at` / `stats_history[].at` are UTC with no
// trailing "Z" - JS parses a bare date-time string as local time, so append it
// ourselves before formatting (API doc, "Gotchas" #1).
export const formatUtcDate = (isoNoZ) => {
  if (!isoNoZ) return null
  try {
    return new Date(`${isoNoZ}Z`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  } catch {
    return null
  }
}

// Instagram reels only carry `duration_seconds` (no ready-made "1:23" string
// like YouTube's `duration_text`) - INSTAGRAM_REEL_API.md §5.
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return null
  const total = Math.round(Number(seconds))
  if (!Number.isFinite(total) || total < 0) return null
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

// Instagram's `published_at` is exact, never estimated (`published_at_is_estimate`
// is always false per §5) but it's a plain timestamp, not YouTube's ready-made
// "2 weeks ago" text - so this computes the same style of relative label
// client-side. Same no-trailing-"Z" gotcha as formatUtcDate above.
export const formatRelativeTime = (isoNoZ) => {
  if (!isoNoZ) return null
  let date
  try {
    date = new Date(`${isoNoZ}Z`)
  } catch {
    return null
  }
  if (Number.isNaN(date.getTime())) return null

  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return formatUtcDate(isoNoZ)

  const minute = 60 * 1000, hour = 60 * minute, day = 24 * hour, month = 30 * day, year = 365 * day
  const bucket = (ms, unit, label) => {
    const n = Math.floor(diffMs / unit)
    return n >= 1 ? `${n} ${label}${n === 1 ? "" : "s"} ago` : null
  }

  if (diffMs < minute) return "Just now"
  if (diffMs < hour) return bucket(diffMs, minute, "minute")
  if (diffMs < day) return bucket(diffMs, hour, "hour")
  if (diffMs < month) return bucket(diffMs, day, "day")
  if (diffMs < year) return bucket(diffMs, month, "month")
  return bucket(diffMs, year, "year")
}

// Generic active-filter counter used for the "Filters (N)" toolbar button -
// works for any platform's filter shape (arrays count their length, booleans
// count 1 when true, strings count 1 when non-blank) so it never needs to
// know each platform's individual filter keys.
export const countActiveFilters = (filters) => {
  let count = 0
  Object.values(filters || {}).forEach((value) => {
    if (Array.isArray(value)) count += value.length
    else if (typeof value === "boolean") { if (value) count += 1 }
    else if (typeof value === "string") { if (value.trim()) count += 1 }
  })
  return count
}
